import { Sun } from 'lucide-react'
import { useI18n } from '../../lib/i18n/i18n-store'
import { isLightColor } from '../../lib/theme/color-utils'
import { useThemeStore } from '../../lib/theme/theme-store'
import './LightModeToggle.css'

/**
 * The light mode button: a light version of the active theme
 * (lib/theme/light.ts), without changing the theme. Pressed = on.
 *
 * Disabled while it is off and the active theme is already light: there would
 * be nothing to force. It stays enabled while on, so it can always be turned
 * off.
 *
 * Lives in the sidebar, in the top bar on phones (no sidebar there), in
 * Settings and in the appearance studio; the command palette toggles it too.
 */
export function LightModeToggle({ withLabel = false, className = '' }: { withLabel?: boolean; className?: string }) {
  const { t } = useI18n()
  const lightMode = useThemeStore((s) => s.lightMode)
  const themeIsLight = useThemeStore((s) => isLightColor(s.getActiveProfile().colors.bgBase))
  const disabled = themeIsLight && !lightMode
  const action = disabled ? t('theme.light.alreadyLight') : lightMode ? t('theme.light.off') : t('theme.light.on')

  return (
    <button
      type="button"
      className={`btn btn--outline btn--toggle${withLabel ? '' : ' btn--icon'} light-toggle${withLabel ? ' light-toggle--label' : ''} ${className}`.trim()}
      aria-pressed={lightMode}
      aria-label={withLabel ? undefined : t('appearance.light.label')}
      title={action}
      disabled={disabled}
      onClick={() => useThemeStore.getState().setLightMode(!lightMode)}
    >
      <Sun size={16} aria-hidden="true" />
      {withLabel && <span>{t('appearance.light.label')}</span>}
    </button>
  )
}
